import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, ApiUnreachableError, type ApiClient } from "../api/client.js";
import type { NewPlanObject, PlanObject, PlanObjectPatch, PlanObjectVersionConflict } from "../api/types.js";
import { conflictMessage, versionConflicts, type ConflictNaming } from "./plan-conflicts.js";
import { KIND_NAMES } from "./plan-objects.js";

/**
 * The plan's objects, held for the drawing and written through to the API.
 *
 * SERVER-SIDE, NOT THIS BROWSER. Unlike the hand-drawn dimensions
 * (`plan-annotations.ts`, localStorage), these are shared design work — the
 * designer lays out radiators, a colleague prices the pipework — so they live
 * with the survey (migration 0032) and every change goes to the API.
 *
 * OPTIMISTIC, WITH ROLLBACK. The drawing changes the moment the designer acts;
 * waiting on a round trip before a dragged radiator lands would make the plan
 * feel broken. When the API refuses or cannot be reached, the change is undone
 * on the drawing and the reason is shown in words — never silently dropped,
 * and never left on screen looking saved when it is not.
 *
 * A new object carries a provisional id until the API answers. It is drawn,
 * dimmed, but cannot be moved or removed until it has its real id — the one
 * thing an edit to it could be sent against.
 *
 * TWO PEOPLE, ONE PLAN (migration 0046). Every object has a version, and each
 * change or removal is sent with the version it was made on. Changes to ONE
 * object go one at a time, in order, so each is made on the version the one
 * before it left — never raced against this page's own earlier save. When a
 * colleague (or this person in another tab) changed or removed the object
 * first, the server refuses: the drawing shows THEIR version (or loses the
 * object they removed), anything still queued for that object is not sent,
 * the list is re-read, and one plain sentence says who did what and when.
 * The refused change is never re-sent over theirs.
 */

/** What the plan needs from wherever objects are kept. The API, in the portal. */
export interface PlanObjectsSource {
  list(): Promise<readonly PlanObject[]>;
  create(input: NewPlanObject): Promise<PlanObject>;
  /** `baseVersion`: the version the change was made on; the API refuses it if the object has moved on. */
  update(id: string, patch: PlanObjectPatch, baseVersion?: number): Promise<PlanObject>;
  remove(id: string, baseVersion?: number): Promise<void>;
}

export function planObjectsFromApi(client: ApiClient, surveyId: string): PlanObjectsSource {
  return {
    list: () => client.planObjects(surveyId),
    create: (input) => client.createPlanObject(surveyId, input),
    update: (id, patch, baseVersion) => client.updatePlanObject(surveyId, id, patch, baseVersion),
    remove: (id, baseVersion) => client.deletePlanObject(surveyId, id, baseVersion),
  };
}

const PROVISIONAL = "provisional-";

/** True for an object the API has not yet given an id. */
export function isProvisional(id: string): boolean {
  return id.startsWith(PROVISIONAL);
}

function reasonOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof ApiUnreachableError) return error.message;
  return "Something went wrong saving it.";
}

function article(kind: PlanObject["kind"]): string {
  return KIND_NAMES[kind].toLowerCase();
}

/** How an object is named when a save to it is refused: "changed this radiator", "took this radiator off the plan". */
export function objectNaming(kind: PlanObject["kind"] | undefined): ConflictNaming {
  const thing = kind ? article(kind) : "object";
  return { thing, removed: `took this ${thing} off the plan` };
}

export type PlanObjectsStatus = "unavailable" | "loading" | "ready" | "failed";

export interface PlanObjectsState {
  readonly status: PlanObjectsStatus;
  readonly objects: readonly PlanObject[];
  /** Why the last change was undone, in words, until dismissed or superseded. */
  readonly error: string | null;
  /** `error` is about someone else's change getting there first, not a failure. */
  readonly conflict: boolean;
  readonly loadError: string | null;
  /** Adds an object; resolves to its saved form, or null if it was refused. */
  readonly add: (input: NewPlanObject) => { provisionalId: string; saved: Promise<PlanObject | null> };
  readonly update: (id: string, patch: PlanObjectPatch) => void;
  readonly remove: (id: string) => void;
  readonly dismissError: () => void;
  readonly reload: () => void;
}

export interface PlanObjectsOptions {
  /** The signed-in user, so a conflict with their own other tab says "You". */
  readonly meId?: string;
}

function applyPatch(object: PlanObject, patch: PlanObjectPatch): PlanObject {
  return {
    ...object,
    ...(patch.spec !== undefined ? { spec: patch.spec } : {}),
    ...(patch.geometry !== undefined ? { geometry: patch.geometry } : {}),
    ...(patch.label !== undefined ? { label: patch.label } : {}),
    ...(patch.storey !== undefined ? { storey: patch.storey } : {}),
  } as PlanObject;
}

export function usePlanObjects(
  source: PlanObjectsSource | undefined,
  surveyId: string,
  options: PlanObjectsOptions = {},
): PlanObjectsState {
  const { meId } = options;
  const [objects, setObjects] = useState<readonly PlanObject[]>([]);
  const [status, setStatus] = useState<PlanObjectsStatus>(source ? "loading" : "unavailable");
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const counter = useRef(0);
  /** Per object, the latest local edit — so an older reply never overwrites a newer drag. */
  const edits = useRef(new Map<string, number>());
  const alive = useRef(true);
  // Versions (0046). Refs: the per-object queues read them between renders.
  /** Per object, the version this page's picture of it is based on. */
  const versions = useRef(new Map<string, number>());
  /** Per object, the tail of its queue of saves, and how many are in it. */
  const chains = useRef(new Map<string, { tail: Promise<void>; size: number }>());
  /** Per object, bumped when a save to it is refused: what was queued behind it is not sent. */
  const epochs = useRef(new Map<string, number>());

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** Records the server's versions, except for objects with saves queued — those were made on what was there. */
  const adoptVersions = useCallback((loaded: readonly PlanObject[]) => {
    for (const object of loaded) {
      if (!chains.current.has(object.id) && typeof object.version === "number") {
        versions.current.set(object.id, object.version);
      }
    }
  }, []);

  useEffect(() => {
    if (!source) {
      setStatus("unavailable");
      setObjects([]);
      return;
    }
    let current = true;
    setStatus("loading");
    setLoadError(null);
    source.list().then(
      (loaded) => {
        if (!current) return;
        adoptVersions(loaded);
        // Keep anything still being saved: it is on the drawing already.
        setObjects((now) => [...loaded, ...now.filter((o) => isProvisional(o.id))]);
        setStatus("ready");
      },
      (err: unknown) => {
        if (!current) return;
        setLoadError(reasonOf(err));
        setStatus("failed");
      },
    );
    return () => {
      current = false;
    };
  }, [source, surveyId, nonce, adoptVersions]);

  /**
   * Runs `task` after every save already queued for `id` — at once when there
   * is none, so a lone change is sent in the same tick as before.
   */
  const enqueue = useCallback((id: string, task: () => Promise<void> | undefined) => {
    const held = chains.current.get(id);
    const start = held ? held.tail.then(task) : Promise.resolve(task());
    const tail = start.catch(() => undefined).then(() => {
      const now = chains.current.get(id);
      if (!now) return;
      if (now.size <= 1) chains.current.delete(id);
      else chains.current.set(id, { tail: now.tail, size: now.size - 1 });
    });
    chains.current.set(id, { tail, size: (held?.size ?? 0) + 1 });
  }, []);

  /** Re-reads the list without a "loading" state, keeping whatever still has saves queued as it is drawn. */
  const refresh = useCallback(() => {
    if (!source) return;
    source.list().then(
      (loaded) => {
        if (!alive.current) return;
        adoptVersions(loaded);
        setObjects((now) => {
          const local = new Map(now.map((o) => [o.id, o] as const));
          const busy = (id: string) => chains.current.has(id);
          const fresh = loaded.flatMap((o) => (busy(o.id) ? (local.has(o.id) ? [local.get(o.id) as PlanObject] : []) : [o]));
          const seen = new Set(loaded.map((o) => o.id));
          return [...fresh, ...now.filter((o) => isProvisional(o.id) || (busy(o.id) && !seen.has(o.id)))];
        });
      },
      // The conflict's own object is already drawn as the server has it.
      () => undefined,
    );
  }, [source, adoptVersions]);

  /** Someone else got there first: show their version, drop what was queued behind ours, say so. */
  const refused = useCallback(
    (id: string, found: PlanObjectVersionConflict, attempted: "change" | "removal", known: PlanObject | undefined) => {
      epochs.current.set(id, (epochs.current.get(id) ?? 0) + 1);
      edits.current.set(id, (edits.current.get(id) ?? 0) + 1);
      const theirs = found.current;
      if (theirs) {
        if (typeof theirs.version === "number") versions.current.set(id, theirs.version);
        setObjects((list) =>
          list.some((o) => o.id === id) ? list.map((o) => (o.id === id ? theirs : o)) : [...list, theirs],
        );
      } else {
        versions.current.delete(id);
        setObjects((list) => list.filter((o) => o.id !== id));
      }
      setConflict(true);
      setError(
        conflictMessage(found, objectNaming((theirs ?? known)?.kind), {
          attempted,
          ...(meId ? { meId } : {}),
        }),
      );
      refresh();
    },
    [meId, refresh],
  );

  const add = useCallback<PlanObjectsState["add"]>(
    (input) => {
      counter.current += 1;
      const provisionalId = `${PROVISIONAL}${counter.current}`;
      if (!source) return { provisionalId, saved: Promise.resolve(null) };
      const now = new Date().toISOString();
      const provisional = {
        ...input,
        id: provisionalId,
        surveyId,
        label: input.label ?? null,
        createdBy: null,
        updatedBy: null,
        createdAt: now,
        updatedAt: now,
      } as PlanObject;
      setObjects((list) => [...list, provisional]);
      setError(null);
      setConflict(false);
      const saved = source.create(input).then(
        (object) => {
          if (typeof object.version === "number") versions.current.set(object.id, object.version);
          if (alive.current) {
            setObjects((list) => list.map((o) => (o.id === provisionalId ? object : o)));
          }
          return object;
        },
        (err: unknown) => {
          if (alive.current) {
            setObjects((list) => list.filter((o) => o.id !== provisionalId));
            setError(
              `The ${article(input.kind)} could not be saved, so it has been taken off the plan. ${reasonOf(err)}`,
            );
          }
          return null;
        },
      );
      return { provisionalId, saved };
    },
    [source, surveyId],
  );

  const update = useCallback<PlanObjectsState["update"]>(
    (id, patch) => {
      if (!source || isProvisional(id)) return;
      let before: PlanObject | undefined;
      setObjects((list) =>
        list.map((o) => {
          if (o.id !== id) return o;
          before = o;
          return applyPatch(o, patch);
        }),
      );
      const edit = (edits.current.get(id) ?? 0) + 1;
      edits.current.set(id, edit);
      setError(null);
      setConflict(false);
      const epoch = epochs.current.get(id) ?? 0;
      enqueue(id, () => {
        // Queued behind a save that was refused: this was made on top of it.
        if ((epochs.current.get(id) ?? 0) !== epoch) return undefined;
        return source.update(id, patch, versions.current.get(id)).then(
          (object) => {
            if (typeof object.version === "number") versions.current.set(id, object.version);
            if (!alive.current || edits.current.get(id) !== edit) return;
            setObjects((list) => list.map((o) => (o.id === id ? object : o)));
          },
          (err: unknown) => {
            if (!alive.current) return;
            const found = versionConflicts<PlanObjectVersionConflict>(err)?.[0];
            if (found) {
              refused(id, found, "change", before);
              return;
            }
            const undone = before;
            if (edits.current.get(id) === edit && undone) {
              setObjects((list) => list.map((o) => (o.id === id ? undone : o)));
            }
            setError(
              `That change to the ${undone ? article(undone.kind) : "object"} could not be saved, so it has been undone. ${reasonOf(err)}`,
            );
          },
        );
      });
    },
    [source, enqueue, refused],
  );

  const remove = useCallback<PlanObjectsState["remove"]>(
    (id) => {
      if (!source || isProvisional(id)) return;
      let removed: { object: PlanObject; index: number } | undefined;
      setObjects((list) => {
        const index = list.findIndex((o) => o.id === id);
        if (index < 0) return list;
        removed = { object: list[index] as PlanObject, index };
        return list.filter((o) => o.id !== id);
      });
      setError(null);
      setConflict(false);
      const epoch = epochs.current.get(id) ?? 0;
      enqueue(id, () => {
        if ((epochs.current.get(id) ?? 0) !== epoch) return undefined;
        return source.remove(id, versions.current.get(id)).then(
          () => {
            versions.current.delete(id);
          },
          (err: unknown) => {
            if (!alive.current) return;
            const found = versionConflicts<PlanObjectVersionConflict>(err)?.[0];
            if (found) {
              refused(id, found, "removal", removed?.object);
              return;
            }
            const back = removed;
            if (back) {
              setObjects((list) => {
                if (list.some((o) => o.id === back.object.id)) return list;
                const next = [...list];
                next.splice(Math.min(back.index, next.length), 0, back.object);
                return next;
              });
            }
            setError(
              `The ${back ? article(back.object.kind) : "object"} could not be removed, so it has been put back. ${reasonOf(err)}`,
            );
          },
        );
      });
    },
    [source, enqueue, refused],
  );

  const dismissError = useCallback(() => {
    setError(null);
    setConflict(false);
  }, []);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { status, objects, error, conflict, loadError, add, update, remove, dismissError, reload };
}
